import React from 'react';
import {
  Card,
  CardContent,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  LoadingSkeleton,
} from '@redbax/ui-kit';
import { ShieldCheck, ShieldX, Info, ChevronLeft, ChevronRight, Package, Box, Gem, Coffee, Wind } from 'lucide-react';
import type { ApprovalRequest } from '@redbax/shared';
import { useVulnerabilitySearch } from '../api/package-policy-hooks';
import { SeverityBadge } from './severity-badge';

interface ApprovalRequestCardProps {
  request: ApprovalRequest;
  onAddToAllowlist: (request: ApprovalRequest) => void;
  onAddToBlocklist: (request: ApprovalRequest) => void;
  isProcessing: boolean;
}

function EcosystemIcon({ ecosystem, className }: { ecosystem: string; className?: string }) {
  const cls = className ?? 'h-4 w-4';
  switch (ecosystem.toLowerCase()) {
    case 'npm': return <Box className={cls} />;
    case 'nuget': return <Package className={cls} />;
    case 'pypi': return <Wind className={cls} />;
    case 'maven': return <Coffee className={cls} />;
    case 'rubygems': return <Gem className={cls} />;
    default: return <Package className={cls} />;
  }
}

function severityVariant(severity: string | null) {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL':
      return 'destructive' as const;
    case 'HIGH':
      return 'warning' as const;
    case 'MEDIUM':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
}

function formatAge(createdAt: string): string {
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function VulnCarousel({
  vulns,
  versionSpecific,
  packageVersion,
}: {
  vulns: import('@redbax/shared').PackageVulnerability[];
  versionSpecific: import('@redbax/shared').PackageVulnerability[];
  packageVersion: string | null;
}) {
  const [idx, setIdx] = React.useState(0);
  const vuln = vulns[idx];
  const affectsVersion = packageVersion ? versionSpecific.includes(vuln) : null;

  const rangeText = React.useMemo(() => {
    if (!vuln?.affectedVersions) return null;
    try {
      const parsed = JSON.parse(vuln.affectedVersions);
      const ranges = parsed.ranges as { events?: { introduced?: string; fixed?: string }[] }[] | undefined;
      if (!ranges?.length) return null;
      return ranges.flatMap((r) =>
        (r.events ?? []).reduce<string[]>((acc, e) => {
          if (e.introduced) acc.push(e.introduced === '0' ? 'all versions' : `>=${e.introduced}`);
          if (e.fixed) acc[acc.length - 1] = `${acc[acc.length - 1]}, <${e.fixed}`;
          return acc;
        }, []),
      ).join(' | ');
    } catch { return null; }
  }, [vuln]);

  return (
    <div className="space-y-3 pt-2">
      {packageVersion && versionSpecific.length === 0 ? (
        <div className="rounded-lg border border-green-ok/30 bg-green-ok/5 px-3 py-2">
          <p className="text-xs text-green-ok font-medium">
            v{packageVersion} is not affected by any of the {vulns.length} known vulnerabilities.
          </p>
        </div>
      ) : packageVersion && versionSpecific.length > 0 ? (
        <div className="rounded-lg border border-red-accent/30 bg-red-subtle/20 px-3 py-2">
          <p className="text-xs text-red-accent font-medium">
            v{packageVersion} is affected by {versionSpecific.length} of {vulns.length} known vulnerabilities.
          </p>
        </div>
      ) : (
        <p className="text-xs text-text-secondary">
          {vulns.length} known vulnerabilit{vulns.length === 1 ? 'y' : 'ies'}
        </p>
      )}

      <div className={`rounded-lg border p-3 ${
        affectsVersion === true ? 'border-red-accent/40 bg-red-subtle/10'
          : affectsVersion === false ? 'border-border-default bg-surface opacity-70'
          : 'border-border-default bg-surface'
      }`}>
        <div className="flex gap-3">
          <div className="flex-1 min-w-0 space-y-1.5">
            <span className="block text-sm font-medium text-text-primary truncate" title={vuln.id}>{vuln.id}</span>

            {vuln.summary && (
              <p className="text-xs text-text-secondary leading-relaxed line-clamp-2">{vuln.summary}</p>
            )}

            {vuln.aliases && vuln.aliases.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {vuln.aliases.map((alias) => (
                  <Badge key={alias} variant="outline" className="text-[10px]">{alias}</Badge>
                ))}
              </div>
            )}

            {rangeText && (
              <p className="text-[11px] text-text-secondary">
                <span className="font-medium">Range:</span> {rangeText}
              </p>
            )}
          </div>

          <div className="shrink-0 flex flex-col items-end gap-1.5 text-right">
            <SeverityBadge severity={vuln.severity} />
            {vuln.cvssScore != null && (
              <span className="text-xs text-text-secondary font-mono">{vuln.cvssScore.toFixed(1)}</span>
            )}
            {affectsVersion === true && (
              <Badge variant="destructive" className="text-[10px] whitespace-nowrap">Affected</Badge>
            )}
            {affectsVersion === false && (
              <span className="text-[10px] text-green-ok font-medium">Safe</span>
            )}
          </div>
        </div>
      </div>

      {vulns.length > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="rounded p-1 text-text-secondary hover:bg-surface-raised disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-text-secondary">{idx + 1} / {vulns.length}</span>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.min(vulns.length - 1, i + 1))}
            disabled={idx === vulns.length - 1}
            className="rounded p-1 text-text-secondary hover:bg-surface-raised disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function VulnerabilityInfoModal({
  open,
  onOpenChange,
  ecosystem,
  packageName,
  packageVersion,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ecosystem: string;
  packageName: string;
  packageVersion: string | null;
}) {
  const { data, isLoading } = useVulnerabilitySearch({
    ecosystem,
    packageName,
    limit: 100,
  });

  const vulns = data?.items ?? [];
  const versionSpecific = packageVersion
    ? vulns.filter((v) => {
        if (!v.affectedVersions) return true;
        try {
          const parsed = JSON.parse(v.affectedVersions);
          const versions = parsed.versions as string[] | undefined;
          if (versions?.length) return versions.includes(packageVersion);
          return true;
        } catch {
          return true;
        }
      })
    : vulns;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-base">Package Vulnerability Report</DialogTitle>
              <DialogDescription className="space-y-0.5 mt-1">
                <span className="block text-sm font-semibold text-text-primary">
                  {packageName}{packageVersion && `@${packageVersion}`}
                </span>
              </DialogDescription>
            </div>
            <div className="flex items-center gap-1.5 text-text-secondary shrink-0 mt-1">
              <EcosystemIcon ecosystem={ecosystem} className="h-4 w-4" />
              <span className="text-xs font-medium">{ecosystem}</span>
            </div>
          </div>
        </DialogHeader>

        {isLoading ? (
          <LoadingSkeleton rows={3} />
        ) : vulns.length === 0 ? (
          <div className="rounded-lg border border-green-ok/30 bg-green-ok/5 px-3 py-4 text-center">
            <p className="text-sm text-green-ok font-medium">No known vulnerabilities found for this package.</p>
          </div>
        ) : (
          <VulnCarousel vulns={vulns} versionSpecific={versionSpecific} packageVersion={packageVersion} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SeverityPills({ vulnerabilities }: { vulnerabilities: ApprovalRequestCardProps['request']['vulnerabilities'] }) {
  if (vulnerabilities.length === 0) return null;
  const counts = { C: 0, H: 0, M: 0, L: 0 };
  for (const v of vulnerabilities) {
    switch (v.severity?.toUpperCase()) {
      case 'CRITICAL': counts.C++; break;
      case 'HIGH': counts.H++; break;
      case 'MEDIUM': counts.M++; break;
      case 'LOW': counts.L++; break;
    }
  }
  return (
    <div className="flex items-center gap-1">
      {counts.C > 0 && <span className="inline-flex items-center rounded-full bg-red-accent/15 px-2 py-0.5 text-[10px] font-semibold text-red-accent">{counts.C} C</span>}
      {counts.H > 0 && <span className="inline-flex items-center rounded-full bg-orange-500/15 px-2 py-0.5 text-[10px] font-semibold text-orange-500">{counts.H} H</span>}
      {counts.M > 0 && <span className="inline-flex items-center rounded-full bg-yellow-500/15 px-2 py-0.5 text-[10px] font-semibold text-yellow-500">{counts.M} M</span>}
      {counts.L > 0 && <span className="inline-flex items-center rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold text-blue-500">{counts.L} L</span>}
    </div>
  );
}

export function ApprovalRequestCard({
  request,
  onAddToAllowlist,
  onAddToBlocklist,
  isProcessing,
}: ApprovalRequestCardProps) {
  const [showVulnInfo, setShowVulnInfo] = React.useState(false);

  return (
    <>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <EcosystemIcon ecosystem={request.ecosystem} className="h-4 w-4 shrink-0 text-text-secondary" />
              <span className="text-sm font-semibold text-text-primary truncate">
                {request.packageName}
              </span>
              {request.packageVersion && (
                <Badge variant="outline" className="shrink-0">v{request.packageVersion}</Badge>
              )}
              <span className="text-xs text-text-secondary shrink-0">
                {formatAge(request.createdAt)}
              </span>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <SeverityPills vulnerabilities={request.vulnerabilities} />

              <button
                type="button"
                onClick={() => setShowVulnInfo(true)}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-text-secondary transition-colors hover:bg-surface-raised hover:text-text-primary"
                title="View vulnerability report"
              >
                <Info className="h-3.5 w-3.5" />
              </button>

              {request.status === 'Pending' && (
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => onAddToBlocklist(request)}
                    disabled={isProcessing}
                    className="h-7 px-2.5 text-xs"
                  >
                    <ShieldX className="mr-1 h-3 w-3" />
                    Block
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onAddToAllowlist(request)}
                    disabled={isProcessing}
                    className="h-7 px-2.5 text-xs"
                  >
                    <ShieldCheck className="mr-1 h-3 w-3" />
                    Allow
                  </Button>
                </div>
              )}
            </div>
          </div>

          {request.requestedByHostname && (
            <p className="text-xs text-text-secondary mt-1.5 pl-6">
              Requested from <span className="font-medium">{request.requestedByHostname}</span>
            </p>
          )}
        </CardContent>
      </Card>

      <VulnerabilityInfoModal
        open={showVulnInfo}
        onOpenChange={setShowVulnInfo}
        ecosystem={request.ecosystem}
        packageName={request.packageName}
        packageVersion={request.packageVersion}
      />
    </>
  );
}
